import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "@/components/site/LocaleLink";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useLang, useL } from "@/context/LanguageContext";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { CATEGORIES } from "@/data/products";
import { stripLang } from "@/lib/lang";

const NavLink = ({ to, children, onClick }) => {
  const { pathname } = useLocation();
  const active = stripLang(pathname) === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className="group relative py-1 text-sm font-medium text-ink transition-colors duration-300 hover:text-forest"
    >
      {children}
      <span
        className={`absolute -bottom-0.5 start-0 h-[2px] bg-gold transition-all duration-300 ${
          active ? "w-full" : "w-0 group-hover:w-full"
        }`}
      />
    </Link>
  );
};

/**
 * Products menu. Opens on hover for pointer users and on click/Enter for
 * keyboard users, closes on Escape or focus leaving the group.
 */
const ProductsMenu = () => {
  const { t } = useLang();
  const L = useL();
  const [open, setOpen] = useState(false);
  const groupRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      setOpen(false);
      triggerRef.current?.focus();
    }
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  // Close once focus moves outside the trigger + panel entirely.
  const onBlur = (e) => {
    if (!groupRef.current?.contains(e.relatedTarget)) close();
  };

  return (
    <div
      ref={groupRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-1 py-1 text-sm font-medium text-ink transition-colors hover:text-forest"
        data-testid="nav-products-trigger"
      >
        {t.nav.products}
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.25 }}
            className="absolute top-full mt-3 w-72 overflow-hidden rounded-xl border border-line bg-white p-2 shadow-[0_20px_50px_rgba(24,58,55,0.12)] start-0"
          >
            {CATEGORIES.map((c) => (
              <Link
                key={c.id}
                to={`/products?category=${c.id}`}
                onClick={close}
                className="flex items-center justify-between rounded-lg px-4 py-3 text-sm text-ink transition-colors hover:bg-sage focus-visible:bg-sage"
                data-testid={`nav-cat-${c.id}`}
              >
                <span className="font-medium">{L(c.name)}</span>
                <ArrowUpRight aria-hidden="true" className="h-4 w-4 text-gold rtl-flip" />
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const { t, toggle, isRtl } = useLang();
  const L = useL();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const drawerRef = useRef(null);

  const closeDrawer = useCallback(() => setOpen(false), []);
  useFocusTrap(drawerRef, open, closeDrawer);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
          scrolled
            ? "bg-cream/95 backdrop-blur-md border-b border-line shadow-[0_2px_20px_rgba(24,58,55,0.04)]"
            : "bg-cream/80 backdrop-blur-sm"
        }`}
        data-testid="navbar"
      >
        <div className="container-x flex h-[74px] items-center justify-between">
          <Logo />

          <nav className="hidden items-center gap-8 lg:flex" aria-label={t.nav.primary}>
            <NavLink to="/">{t.nav.home}</NavLink>
            <ProductsMenu />
            <NavLink to="/about">{t.nav.about}</NavLink>
            <NavLink to="/support">{t.nav.support}</NavLink>
            <NavLink to="/contact">{t.nav.contact}</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggle}
              data-testid="language-toggle"
              lang={isRtl ? "en" : "ar"}
              aria-label={t.common.switchLanguage}
              className="rounded-full border border-forest/25 px-4 py-1.5 text-xs font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-cream"
            >
              {t.common.langLabel}
            </button>
            <Link
              to="/products"
              className="hidden rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-dark sm:inline-flex"
              data-testid="nav-cta-browse"
            >
              {t.nav.browse}
            </Link>
            <button
              type="button"
              className="lg:hidden text-forest"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label={t.nav.openMenu}
              aria-expanded={open}
            >
              <Menu aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-forest-deep/40 backdrop-blur-sm lg:hidden"
            onClick={closeDrawer}
          >
            <motion.div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label={t.nav.primary}
              initial={{ x: isRtl ? "-100%" : "100%" }}
              animate={{ x: 0 }}
              exit={{ x: isRtl ? "-100%" : "100%" }}
              transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-y-0 end-0 flex w-[82%] max-w-sm flex-col overflow-y-auto bg-cream p-7"
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between">
                <Logo />
                <button
                  type="button"
                  onClick={closeDrawer}
                  data-testid="mobile-menu-close"
                  aria-label={t.nav.closeMenu}
                >
                  <X aria-hidden="true" className="h-6 w-6 text-forest" />
                </button>
              </div>
              <nav className="mt-10 flex flex-col gap-1">
                {[
                  { to: "/", label: t.nav.home },
                  { to: "/products", label: t.nav.products },
                  { to: "/about", label: t.nav.about },
                  { to: "/support", label: t.nav.support },
                  { to: "/contact", label: t.nav.contact },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={stripLang(pathname) === item.to ? "page" : undefined}
                    className="border-b border-line py-4 font-display text-2xl font-bold text-forest"
                    data-testid={`mobile-link-${item.to}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-6">
                <p className="eyebrow mb-3 text-stone">{t.nav.categories}</p>
                <div className="flex flex-col gap-2">
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.id}
                      to={`/products?category=${c.id}`}
                      className="text-sm text-stone transition-colors hover:text-forest"
                    >
                      {L(c.name)}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
